import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { authAPI } from "../lib/api";

const ACCENT = "#2563EB";
const ACCENT_DARK = "#1D4ED8";
const BG = "#06080F";
const BORDER = "#1E2433";
const TEXT_PRIMARY = "#F9FAFB";
const TEXT_MUTED = "#6B7280";
const TEXT_DIM = "#374151";

const Logo = ({ onClick }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 40, cursor: "pointer" }} onClick={onClick}>
    <span style={{ fontWeight: 700, fontSize: 17, color: TEXT_PRIMARY, letterSpacing: "-0.03em", fontFamily: "'DM Sans', sans-serif" }}>
      INFERENCE
    </span>
    <span style={{ width: 5, height: 5, borderRadius: "50%", background: ACCENT, marginBottom: 6 }} />
  </div>
);

const Input = ({ label, type = "text", placeholder, value, onChange, autoComplete }) => (
  <div style={{ marginBottom: 18 }}>
    <label style={{ display: "block", fontSize: 12, fontWeight: 500, color: "#9CA3AF", marginBottom: 6, letterSpacing: "0.02em" }}>
      {label}
    </label>
    <input
      type={type} placeholder={placeholder} value={value} onChange={onChange} autoComplete={autoComplete}
      data-testid={`reset-input-${label.toLowerCase().replace(/\s+/g, "-")}`}
      style={{
        width: "100%", background: "#0D1117", border: `1px solid ${BORDER}`,
        borderRadius: 8, padding: "11px 14px", fontSize: 14, color: TEXT_PRIMARY,
        outline: "none", fontFamily: "'DM Sans', sans-serif", transition: "border-color 0.2s, box-shadow 0.2s",
      }}
      onFocus={e => { e.target.style.borderColor = ACCENT; e.target.style.boxShadow = `0 0 0 3px ${ACCENT}18`; }}
      onBlur={e => { e.target.style.borderColor = BORDER; e.target.style.boxShadow = "none"; }}
    />
  </div>
);

const BtnPrimary = ({ children, onClick, loading, disabled, testId }) => (
  <button
    onClick={onClick} disabled={loading || disabled}
    data-testid={testId}
    style={{
      width: "100%",
      background: loading || disabled ? "#1E3A6E" : ACCENT,
      color: loading || disabled ? "#6B9AE0" : "#fff",
      border: "none", borderRadius: 8, padding: "12px 20px",
      fontSize: 14, fontWeight: 600, cursor: loading || disabled ? "not-allowed" : "pointer",
      fontFamily: "'DM Sans', sans-serif", transition: "background 0.2s",
      letterSpacing: "-0.01em", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
    }}
    onMouseEnter={e => { if (!loading && !disabled) e.target.style.background = ACCENT_DARK; }}
    onMouseLeave={e => { if (!loading && !disabled) e.target.style.background = ACCENT; }}
  >
    {loading ? (
      <>
        <span style={{ width: 14, height: 14, border: "2px solid #6B9AE0", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
        Processing…
      </>
    ) : children}
  </button>
);

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [validating, setValidating] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Validate the token on mount
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!token) {
        if (!cancelled) { setTokenValid(false); setValidating(false); }
        return;
      }
      try {
        const res = await authAPI.validateResetToken(token);
        if (!cancelled) setTokenValid(!!res.data?.valid);
      } catch (err) {
        if (!cancelled) setTokenValid(false);
      } finally {
        if (!cancelled) setValidating(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [token]);

  const handleSubmit = async () => {
    if (!newPassword || !confirmPassword) {
      toast.error("Please fill in both password fields.");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }
    setSubmitting(true);
    try {
      await authAPI.resetPassword(token, newPassword);
      setSuccess(true);
      toast.success("Password reset successfully.");
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      console.error("Reset password failed:", err);
      toast.error("Could not reset password. Please request a new link.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif", background: BG, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", padding: "48px 24px" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=DM+Mono:wght@400;500&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        input::placeholder { color: #374151; }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div style={{ width: "100%", maxWidth: 360 }}>
        <Logo onClick={() => navigate("/")} />

        {validating ? (
          <div data-testid="reset-validating" style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 0" }}>
            <span style={{ width: 22, height: 22, border: "2px solid #2563EB33", borderTopColor: "#2563EB", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
          </div>
        ) : !tokenValid ? (
          <div data-testid="reset-invalid-token">
            <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.025em", marginBottom: 6 }}>
              Reset link expired
            </h1>
            <p style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 24, lineHeight: 1.6 }}>
              This reset link has expired or is invalid. Request a new one.
            </p>
            <BtnPrimary onClick={() => navigate("/login")} testId="reset-back-to-login">
              Back to sign in
            </BtnPrimary>
          </div>
        ) : success ? (
          <div data-testid="reset-success">
            <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.025em", marginBottom: 6 }}>
              Password reset
            </h1>
            <p style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 24, lineHeight: 1.6 }}>
              Password reset successfully. Redirecting to login.
            </p>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "12px 0" }}>
              <span style={{ width: 18, height: 18, border: "2px solid #2563EB33", borderTopColor: "#2563EB", borderRadius: "50%", display: "inline-block", animation: "spin 0.8s linear infinite" }} />
            </div>
          </div>
        ) : (
          <div data-testid="reset-form">
            <h1 style={{ fontSize: 22, fontWeight: 700, color: TEXT_PRIMARY, letterSpacing: "-0.025em", marginBottom: 6 }}>
              Choose a new password
            </h1>
            <p style={{ fontSize: 13, color: TEXT_MUTED, marginBottom: 24, lineHeight: 1.6 }}>
              Set a new password for your Inference account. Minimum 8 characters.
            </p>
            <div onKeyDown={e => { if (e.key === "Enter") handleSubmit(); }}>
              <Input label="New password" type="password" placeholder="Min. 8 characters" value={newPassword} onChange={e => setNewPassword(e.target.value)} autoComplete="new-password" />
              <Input label="Confirm new password" type="password" placeholder="Re-enter new password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} autoComplete="new-password" />
            </div>
            <BtnPrimary onClick={handleSubmit} loading={submitting} disabled={!newPassword || !confirmPassword} testId="reset-submit-btn">
              Reset Password
            </BtnPrimary>
            <p style={{ fontSize: 12, color: TEXT_DIM, textAlign: "center", marginTop: 18 }}>
              <span onClick={() => navigate("/login")} style={{ color: ACCENT, cursor: "pointer" }}>← Back to sign in</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
